import { createStreamTrackEventSubscriptions, createTrackDevicesChanges, extractConstraintsWithKeys, findDeviceFromConstraints, relaxInputConstraint, resolveMediaDeviceConstraints, shouldRequestDevice, toMediaDeviceInputKind, } from '@pexip/media-control';
import { calculateMaxBlurPass } from '@pexip/media-processor';
import { hasOwn, assert, isEmpty } from '@pexip/utils';
import { internalSignals } from './signals';
export const makeDeriveDeviceStatus = (constraints) => (audio, video, both) => {
    if (constraints.audio) {
        if (constraints.video) {
            return both;
        }
        return audio;
    }
    if (constraints.video) {
        return video;
    }
    return both;
};
export const createMediaTrack = (trackInit) => {
    const trackConsistency = trackInit.track === undefined ||
        (trackInit.kind === trackInit.input?.kind &&
            trackInit.kind === toMediaDeviceInputKind(trackInit.track));
    assert(trackConsistency, `Inconsistent track kind: ${trackInit.input?.kind} ${trackInit.track && toMediaDeviceInputKind(trackInit.track)} vs ${trackInit.kind}`);
    assert(!hasOwn(trackInit, 'mute') ||
        (hasOwn(trackInit, 'mute') && hasOwn(trackInit, 'overrideMute')), 'Inconsistent overrideMute mute()');
    assert(!hasOwn(trackInit, 'muted') ||
        (hasOwn(trackInit, 'muted') && hasOwn(trackInit, 'overrideMute')), 'Inconsistent overrideMute muted');
    const currentConstraints = typeof trackInit.constraints === 'boolean' ? {} : trackInit.constraints;
    // Find the original source track through the linked list
    let sourceMediaTrack = trackInit.previousMediaTrack;
    while (sourceMediaTrack?.previousMediaTrack) {
        sourceMediaTrack = sourceMediaTrack.previousMediaTrack;
    }
    const getConstraints = () => {
        if (trackInit.constraints === false) {
            return false;
        }
        return {
            ...currentConstraints,
            ...trackInit.track?.getConstraints(),
        };
    };
    const getSettings = () => {
        return {
            ...trackInit.previousMediaTrack?.getSettings(),
            ...trackInit.track?.getSettings(),
            ...(trackInit.getSettings?.() ?? {}),
        };
    };
    // Suscribe track events
    let trackUnsubscribe;
    if (trackInit.track) {
        trackUnsubscribe = createStreamTrackEventSubscriptions(trackInit.track, {
            ended(track) {
                assert(track.id === trackInit.track?.id, 'Same Track');
                void mediaTrack.release();
                trackInit.signals?.onMediaTrackStopped?.emit(mediaTrack);
            },
            mute(track) {
                assert(track.id === trackInit.track?.id, 'Same Track');
                trackInit.signals?.onMediaTrackSuspended?.emit(mediaTrack);
            },
            unmute(track) {
                assert(track.id === trackInit.track?.id, 'Same Track');
                trackInit.signals?.onMediaTrackResumed?.emit(mediaTrack);
            },
        });
    }
    const mediaTrack = {
        get kind() {
            return trackInit.kind;
        },
        get id() {
            return trackInit.id ?? trackInit.track?.id ?? trackInit.kind;
        },
        get track() {
            return trackInit.track;
        },
        get input() {
            if (!trackInit.track) {
                return undefined;
            }
            return trackInit.input;
        },
        get expectedInput() {
            return trackInit.expectedInput;
        },
        get label() {
            return trackInit.label;
        },
        get previousMediaTrack() {
            return trackInit.previousMediaTrack;
        },
        get muted() {
            if (trackInit.overrideMute) {
                return trackInit.muted;
            }
            if (trackInit.track) {
                return !trackInit.track.enabled;
            }
            return undefined;
        },
        get stopped() {
            return trackInit.track && isTrackEnded(trackInit.track);
        },
        get suspended() {
            // Any track that is suspended in the chain will cause this track to be suspended
            return (trackInit.previousMediaTrack?.suspended ||
                trackInit.track?.muted);
        },
        get source() {
            return sourceMediaTrack ?? this;
        },
        mute(toMute, soft) {
            if (this.muted === toMute) {
                return;
            }
            if (trackInit.overrideMute) {
                trackInit.mute?.(toMute, this, soft);
            }
            else {
                if (trackInit.track) {
                    trackInit.track.enabled = !toMute;
                }
                trackInit.previousMediaTrack?.mute(toMute, soft);
            }
            trackInit.signals?.onMediaTrackMuted?.emit(this);
        },
        getSettings,
        getConstraints() {
            return getConstraints();
        },
        clone(signals, label) {
            return createMediaTrack({
                kind: this.kind,
                label: [label, this.label]
                    .flatMap(a => (a ? [a] : []))
                    .join('|'),
                input: this.input,
                constraints: getConstraints(),
                track: this.track?.clone(),
                // When Cloning a MediaTrack, overrideMute should be turned off since
                // it might corrupt the logic of its original media pipeline
                overrideMute: false,
                signals,
            });
        },
        async applyConstraints(constraints) {
            const resolvedConstraints = resolveMediaDeviceConstraints(constraints);
            if (typeof resolvedConstraints === 'boolean' ||
                resolvedConstraints === undefined) {
                return Promise.resolve();
            }
            await trackInit.previousMediaTrack?.applyConstraints(resolvedConstraints);
            await trackInit.applyConstraints?.(constraints);
            if (trackInit.track) {
                if ('contentHint' in resolvedConstraints &&
                    typeof resolvedConstraints.contentHint === 'string' &&
                    trackInit.track.contentHint !==
                        resolvedConstraints.contentHint) {
                    trackInit.track.contentHint =
                        resolvedConstraints.contentHint;
                }
                const settings = trackInit.track.getSettings();
                const reducedConstraints = Object.keys(resolvedConstraints).reduce((accm, key) => {
                    const constraintKey = key;
                    if (constraintKey in settings) {
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment --- Type issue and need to be fixed when we have time
                        // @ts-expect-error
                        accm[constraintKey] =
                            resolvedConstraints[constraintKey];
                    }
                    return accm;
                }, {});
                // Firefox will throw an error if passing an empty constraint object
                if (!isEmpty(reducedConstraints)) {
                    await trackInit.track.applyConstraints(reducedConstraints);
                }
            }
            // Update the current constraints
            Object.assign(currentConstraints, resolvedConstraints);
        },
        async release() {
            trackUnsubscribe?.();
            trackInit.track?.stop();
            await trackInit.release?.();
            await trackInit.previousMediaTrack?.release();
            trackInit.signals?.onMediaTrackStopped?.emit(this);
        },
        toJSON() {
            return {
                label: this.label,
                track: this.track,
                input: this.input,
                previousMediaTrack: this.previousMediaTrack,
                constraints: this.getConstraints(),
                settings: this.getSettings(),
            };
        },
    };
    return mediaTrack;
};
export const createTrackProcessingPipeline = (processors) => async (track) => {
    let lastTrack = track;
    for (const process of processors) {
        if (lastTrack.track?.readyState !== 'live') {
            continue;
        }
        lastTrack = await process(lastTrack);
    }
    return lastTrack;
};
/**
 * Interpret provided input to resolve to a MediaDeviceInfoLike when possible
 * otherwise `undefined`
 */
export const interpretInput = (input, getCurrentInput) => {
    if (input === true || input === undefined) {
        return getCurrentInput();
    }
    if (input === false) {
        return undefined;
    }
    return input;
};
export const findExpectedInput = (devices, constraints, input, kind) => {
    const relaxedConstraints = relaxInputConstraint(kind, constraints, devices);
    const { device: [[device] = []], } = extractConstraintsWithKeys(['device'])(relaxedConstraints);
    // The result from `findDeviceFromConstraints` has more restrictive
    // result since it also consider if the device can be found from the
    // device list
    const found = device ?? findDeviceFromConstraints(constraints, devices);
    const expectedInput = interpretInput(found, () => input);
    return expectedInput;
};
/**
 * A utility function to check if the provided track is muted. There are
 * 2 factors to be considered: `MediaStreamTrack['muted']` and `MediaStreamTrack['enabled']`.
 *
 * ```md
 * | muted \ enabled | true  | false |
 * |-----------------| ----- | ----- |
 * |     true        | true  | true  |
 * |     false       | false | true  |
 * ```
 *
 * @param track - The tracks can be got from `MediaStream['getAudioStats']` or
 * `MediaStream['getVideoTracks']`
 *
 * @returns `true` means muted, `false` means not muted and `undefined` means
 * there is no track to check
 */
export const isTrackMuted = (track) => {
    if (!track) {
        return undefined;
    }
    return track.muted || !track.enabled;
};
export const isTrackEnded = (track) => track.readyState === 'ended';
export const buildMedia = (mediaInit, onDevicesChanged = internalSignals.onDevicesChanged) => {
    const props = {
        originalConstraints: mediaInit.originalConstraints ?? mediaInit.constraints,
        status: mediaInit.status,
        devices: mediaInit.devices,
        stream: mediaInit.stream ?? new MediaStream(),
        // `undefined` is used to avoid double state overriding,
        // because 2 subsequent `susppend` events could invalidate the previous assignment
        audioAlreadyMuted: undefined,
        videoAlreadyMuted: undefined,
        audioTrack: mediaInit.tracks
            .flatMap(track => {
            return track.kind === 'audioinput' ? [track] : [];
        })
            .at(0),
        videoTrack: mediaInit.tracks
            .flatMap(track => (track.kind === 'videoinput' ? [track] : []))
            .at(0),
    };
    const getConstraints = () => {
        const audio = props.audioTrack?.getConstraints() ?? mediaInit.constraints.audio;
        const video = props.videoTrack?.getConstraints() ?? mediaInit.constraints.video;
        return { audio: audio, video: video };
    };
    const updateDevices = (devices) => {
        props.devices = devices;
    };
    // Two-hand control to avoid leaking of the media
    // When the track is suspended by the system, we also mute it if it is not already muted.
    // As it is possible that the track could still flow data even if it is system muted
    const handleSuspended = (track) => {
        switch (track.kind) {
            case 'audioinput': {
                if (props.audioAlreadyMuted === undefined) {
                    props.audioAlreadyMuted = props.audioTrack?.muted === true;
                }
                if (!props.audioAlreadyMuted) {
                    props.audioTrack?.mute(true, true);
                }
                break;
            }
            case 'videoinput': {
                if (props.videoAlreadyMuted === undefined) {
                    props.videoAlreadyMuted = props.videoTrack?.muted === true;
                }
                if (!props.videoAlreadyMuted) {
                    props.videoTrack?.mute(true, true);
                }
                break;
            }
        }
    };
    // When the track is resumed by the system, we only unmute the track
    // if an only if it is muted by the suspended event
    const handleResumed = (track) => {
        switch (track.kind) {
            case 'audioinput': {
                assert(props.audioAlreadyMuted !== undefined, 'Audio Resume should not be triggerred before Suspended');
                if (props.audioTrack?.suspended === false) {
                    if (!props.audioAlreadyMuted) {
                        props.audioTrack.mute(false);
                    }
                    props.audioAlreadyMuted = undefined;
                }
                break;
            }
            case 'videoinput': {
                assert(props.videoAlreadyMuted !== undefined, 'Video Resume should not be triggerred before Suspended');
                if (props.videoTrack?.suspended === false) {
                    if (!props.videoAlreadyMuted) {
                        props.videoTrack.mute(false);
                    }
                    props.videoAlreadyMuted = undefined;
                }
                break;
            }
        }
    };
    const subscribe = () => [
        onDevicesChanged.add(updateDevices),
        mediaInit.signals?.onMediaTrackSuspended?.add(handleSuspended),
        mediaInit.signals?.onMediaTrackResumed?.add(handleResumed),
    ];
    let subscriptions = subscribe();
    const unsubscribe = () => {
        for (const unsubscribe of subscriptions) {
            unsubscribe?.();
        }
        subscriptions.length = 0;
    };
    return {
        get id() {
            return props.stream.id;
        },
        get active() {
            return !!props.stream.active;
        },
        get devices() {
            return props.devices;
        },
        get stream() {
            return props.stream;
        },
        get expectedAudioInput() {
            return props.audioTrack?.expectedInput;
        },
        get expectedVideoInput() {
            return props.videoTrack?.expectedInput;
        },
        get audioInput() {
            return props.audioTrack?.input;
        },
        get videoInput() {
            return props.videoTrack?.input;
        },
        get status() {
            return props.status;
        },
        set status(status) {
            if (props.status !== status) {
                props.status = status;
                mediaInit.signals?.onStatusChanged?.emit(status);
            }
        },
        get audioMuted() {
            return props.audioTrack?.muted;
        },
        get videoMuted() {
            return props.videoTrack?.muted;
        },
        getConstraints,
        getOriginalConstraints() {
            return props.originalConstraints;
        },
        isAudioInputUnavailable() {
            return !props.devices.anyAuthorizedDevice('audioinput');
        },
        isVideoInputUnavailable() {
            return !props.devices.anyAuthorizedDevice('videoinput');
        },
        setOriginalConstraints(constraints) {
            props.originalConstraints = constraints;
        },
        requestedAudio() {
            return (Boolean(props.audioTrack?.track) ||
                (Boolean(props.originalConstraints.audio) &&
                    props.devices.size('audioinput') > 0));
        },
        requestedVideo() {
            return (Boolean(props.videoTrack?.track) ||
                (Boolean(props.originalConstraints.video) &&
                    props.devices.size('videoinput') > 0));
        },
        muteAudio(mute) {
            if (!props.audioTrack || props.audioTrack.suspended) {
                return;
            }
            props.audioTrack.mute(mute);
        },
        muteVideo(mute) {
            if (!props.videoTrack || props.videoTrack.suspended) {
                return;
            }
            props.videoTrack?.mute(mute);
        },
        applyConstraints: async (constraints) => {
            await Promise.all([
                typeof constraints.audio !== 'object'
                    ? Promise.resolve()
                    : props.audioTrack?.applyConstraints(constraints.audio),
                typeof constraints.video !== 'object'
                    ? Promise.resolve()
                    : props.videoTrack?.applyConstraints(constraints.video),
            ]);
        },
        async release() {
            unsubscribe();
            await Promise.all([
                props.audioTrack?.release(),
                props.videoTrack?.release(),
            ]);
        },
        getSettings: () => ({
            audio: props.audioTrack?.getSettings(),
            video: props.videoTrack?.getSettings(),
        }),
        clone(signals, label) {
            const tracks = [props.audioTrack, props.videoTrack].flatMap(track => {
                if (!track?.source) {
                    return [];
                }
                const cloned = track.source.clone(signals, label);
                return [cloned];
            });
            const stream = this.stream &&
                new MediaStream(tracks.flatMap(track => (track.track ? [track.track] : [])));
            const clonedMedia = buildMedia({
                constraints: this.getConstraints(),
                devices: this.devices,
                originalConstraints: this.getOriginalConstraints(),
                permission: mediaInit.permission,
                signals,
                status: this.status,
                stream,
                tracks,
            });
            return clonedMedia;
        },
        getTracks() {
            return [props.audioTrack, props.videoTrack].flatMap(track => track ? [track] : []);
        },
        getAudioTracks() {
            return props.audioTrack ? [props.audioTrack] : [];
        },
        getVideoTracks() {
            return props.videoTrack ? [props.videoTrack] : [];
        },
        isCurrentTrack(track) {
            return track === props.audioTrack || track === props.videoTrack;
        },
        addTrack(track) {
            switch (track.kind) {
                case 'audioinput':
                    if (track.id === props.audioTrack?.id) {
                        return;
                    }
                    props.audioTrack = track;
                    break;
                case 'videoinput':
                    if (track.id === props.videoTrack?.id) {
                        return;
                    }
                    props.videoTrack = track;
                    break;
                default:
                    break;
            }
            if (subscriptions.length === 0) {
                subscriptions = subscribe();
            }
            if (track.track) {
                props.stream.addTrack(track.track);
                mediaInit.signals?.onAddTrack?.emit(track.track);
                props.stream.dispatchEvent(new MediaStreamTrackEvent('addtrack', { track: track.track }));
                if (track.muted) {
                    mediaInit.signals?.onMediaTrackMuted?.emit(track);
                }
                if (track.suspended) {
                    mediaInit.signals?.onMediaTrackSuspended?.emit(track);
                }
                if (track.stopped) {
                    mediaInit.signals?.onMediaTrackStopped?.emit(track);
                }
            }
        },
        removeTrack(track) {
            let removed = false;
            if (track.id === props.audioTrack?.id) {
                props.audioTrack = undefined;
                removed = true;
            }
            if (track.id === props.videoTrack?.id) {
                props.videoTrack = undefined;
                removed = true;
            }
            if (removed && track.track) {
                props.stream.removeTrack(track.track);
                mediaInit.signals?.onRemoveTrack?.emit(track.track);
                props.stream.dispatchEvent(new MediaStreamTrackEvent('removetrack', {
                    track: track.track,
                }));
            }
            if (subscriptions.length) {
                unsubscribe();
            }
        },
        toJSON() {
            return {
                constraints: this.getConstraints(),
                devices: this.devices,
                stream: this.stream,
                audioInput: this.audioInput,
                videoInput: this.videoInput,
                expectedAudioInput: this.expectedAudioInput,
                expectedVideoInput: this.expectedVideoInput,
                status: this.status,
                audioMuted: this.audioMuted,
                videoMuted: this.videoMuted,
            };
        },
    };
};
/**
 * Shallow copy the provided object and override with provided overriding
 *
 * @param original - Original object
 * @param overriding - Object of the same type to override the original
 *
 * @returns a shallow copied object
 */
export const shallowCopy = (original, overriding) => {
    const copy = Object.create(Object.getPrototypeOf(original), Object.getOwnPropertyDescriptors(original));
    return Object.defineProperties(copy, Object.getOwnPropertyDescriptors(overriding));
};
export const getDevicesChanges = (prev, next) => {
    const trackChanges = createTrackDevicesChanges(prev);
    return trackChanges(next);
};
export const AUDIO_SETTINGS_KEYS = [
    'denoise',
    'contentHint',
];
export const VIDEO_SETTINGS_KEYS = [
    'frameRate',
    'videoSegmentation',
    'videoSegmentationModel',
    'foregroundThreshold',
    'backgroundBlurAmount',
    'edgeBlurAmount',
    'maskCombineRatio',
    'backgroundImageUrl',
    'contentHint',
];
export const MIXING_SETTINGS_KEYS = [
    'mixWithAdditionalMedia',
];
export const PAN_TILT_ZOOM_SETTINGS_KEYS = [
    'pan',
    'tilt',
    'zoom',
];
export const hasSettingsChanged = (keysToLookFor) => {
    const cache = {};
    return (settingsA, settingsB) => {
        if (cache.result !== undefined &&
            cache.settingsA === settingsA &&
            cache.settingsB === settingsB) {
            return cache.result;
        }
        cache.settingsA = settingsA;
        cache.settingsB = settingsB;
        for (const key of keysToLookFor) {
            if (settingsA === settingsB) {
                cache.result = false;
                return cache.result;
            }
            if (settingsA === undefined || settingsB === undefined) {
                cache.result = true;
                return cache.result;
            }
            if (settingsA[key] !== settingsB[key]) {
                cache.result = true;
                return cache.result;
            }
        }
        cache.result = false;
        return cache.result;
    };
};
export const isApplyingRenderEffect = (effect) => {
    switch (effect) {
        case 'blur':
        case 'overlay':
            return true;
        default:
            return false;
    }
};
export const getSettingsFromKeys = (keys, settings) => {
    if (!settings) {
        return settings;
    }
    const result = {};
    for (const key of keys) {
        if (settings[key] !== undefined) {
            // @ts-expect-error --- Type issue and need to be fixed when we have time
            result[key] = settings[key];
        }
    }
    return result;
};
export const diffSettings = (keysToLookFor) => {
    const cache = {};
    return (settingsA, settingsB) => {
        // Same as before
        if (cache.settingsA === settingsA && cache.settingsB === settingsB) {
            return cache.diff;
        }
        cache.settingsA = settingsA;
        cache.settingsB = settingsB;
        // Nothing changed
        if (settingsA === settingsB) {
            cache.diff = undefined;
            return cache.diff;
        }
        if (settingsA === undefined || settingsB === undefined) {
            const settings = settingsA ?? settingsB;
            for (const key of keysToLookFor) {
                if (settings?.[key] !== undefined) {
                    if (cache.diff === undefined) {
                        cache.diff = {};
                    }
                    // @ts-expect-error --- Type issue and need to be fixed when we have time
                    cache.diff[key] = settings[key];
                }
            }
            return cache.diff;
        }
        for (const key of keysToLookFor) {
            if (settingsA[key] !== settingsB[key]) {
                if (cache.diff === undefined) {
                    cache.diff = {};
                }
                // @ts-expect-error --- Type issue and need to be fixed when we have time
                cache.diff[key] = settingsB[key];
            }
        }
        return cache.diff;
    };
};
export const mergeSettings = (settingsA, settingsB) => {
    if (settingsA === undefined) {
        return settingsB;
    }
    if (settingsB === undefined) {
        return settingsA;
    }
    return { ...settingsA, ...settingsB };
};
/**
 * A function to get the blur kernel size of image height
 *
 * @param percentage - The percentage of image height to calculate the blur
 * kernel size
 * @param height - The image height
 * @param lowestProcessingHeight - The lowest image height that is processed by the consumer, used to adjust the blur kernel size across different resolutions
 * @param max - The upper bound
 *
 * @returns blur kernel size
 */
export const getBlurKernelSize = (percentage, height, lowestProcessingHeight, max = calculateMaxBlurPass(height)) => {
    if (height <= 0 || percentage <= 0 || max <= 0) {
        return 0;
    }
    const minHeightMaxBlurPass = lowestProcessingHeight
        ? calculateMaxBlurPass(lowestProcessingHeight)
        : max;
    return Math.min(Math.ceil(percentage * 0.01 * max) +
        Math.max(0, max - minHeightMaxBlurPass), max);
};
/**
 * Apply the content hint to the track
 *
 * @param hint - Content hint
 * @param track - The track to be applied
 */
export const applyContentHint = (hint) => (track) => {
    if (hint !== undefined && hint !== track.contentHint) {
        track.contentHint = hint;
    }
};
/**
 * Check if the browser supports PTZ feature
 * @returns true if the browser supports PTZ feature, otherwise false
 */
export const hasPtzFeature = () => {
    const supports = navigator.mediaDevices.getSupportedConstraints();
    return (hasOwn(supports, 'pan') &&
        hasOwn(supports, 'tilt') &&
        hasOwn(supports, 'zoom'));
};
export const refineMediaConstraints = (state) => {
    switch (state.permission) {
        case 'denied':
            return false;
        case 'prompt':
        case 'granted': {
            const requestNeeded = shouldRequestDevice({
                kind: state.kind,
                permission: state.permission,
                currentDevices: state.currentDevices,
                request: state.request,
                tracks: state.currentMediaTracks.flatMap(track => track.source?.track ? [track.source.track] : []),
            });
            if (requestNeeded || state.force) {
                return state.request;
            }
            if (state.request) {
                // We already have all we need, skip the request
                return undefined;
            }
            return state.request;
        }
        default:
            return state.request;
    }
};
export const createMediaProcessor = ({ audioProcessors, videoProcessors, onProcessingError, }) => {
    const processAudioTrack = createTrackProcessingPipeline(audioProcessors);
    const processVideoTrack = createTrackProcessingPipeline(videoProcessors);
    const processTrack = async (track) => {
        try {
            switch (track.kind) {
                case 'audioinput':
                    return await processAudioTrack(track);
                case 'videoinput':
                    return await processVideoTrack(track);
                default:
                    assert(false, `Unexpected track kind: ${track.kind}`);
            }
        }
        catch (error) {
            if (error instanceof Error) {
                onProcessingError?.(error, track);
            }
            return track;
        }
    };
    return async (newTracks) => {
        return await Promise.all(newTracks.map(track => processTrack(track)));
    };
};
